import * as React from 'react'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Checkbox from '@mui/material/Checkbox'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import { Tooltip } from '@mui/material'

function not(a: Transferable[], b: Transferable[]) {
  return a.filter((value) => b.indexOf(value) === -1)
}

function intersection(a: Transferable[], b: Transferable[]) {
  return a.filter((value) => b.indexOf(value) !== -1)
}

export interface ITransferable {
  id: number | string
  name: string
  description?: string
}

export type Transferable = ITransferable | string

function asITransferable(maybeString: Transferable): ITransferable {
  return typeof maybeString === 'string'
    ? { id: maybeString, name: maybeString }
    : (maybeString as ITransferable)
}

interface IProps {
  list: Transferable[] | null
  selected: Transferable[]
  setSelected: any
}

export default function TransferList(props: IProps) {
  const [checked, setChecked] = React.useState<Transferable[]>([])
  const [left, setLeft] = React.useState<Transferable[]>(props.list ?? [])
  const [right, setRight] = React.useState<Transferable[]>(props.selected)

  const leftChecked = intersection(checked, left)
  const rightChecked = intersection(checked, right)

  //console.log('TransferList', { list: props.list, checked, left, right, leftChecked, rightChecked })

  const handleToggle = (value: Transferable) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const handleAllRight = () => {
    setRight(right.concat(left))
    setLeft([])
    props.setSelected(right.concat(left))
  }

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked))
    setLeft(not(left, leftChecked))
    setChecked(not(checked, leftChecked))
    props.setSelected(right.concat(leftChecked))
  }

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked))
    setRight(not(right, rightChecked))
    setChecked(not(checked, rightChecked))
    props.setSelected(not(right, rightChecked))
  }

  const handleAllLeft = () => {
    setLeft(left.concat(right))
    setRight([])
    props.setSelected([])
  }

  const customList = (items: Transferable[], e2e: string) => (
    <Paper sx={{ width: 200, height: 230, overflow: 'auto' }}>
      <List dense component="div" role="list" data-e2e={'transfer-list-' + e2e}>
        {items.map((value: Transferable) => {
          const data = asITransferable(value)
          const labelId = `transfer-list-item-${data.id}-label`

          return (
            <ListItem
              key={data.id}
              role="listitem"
              button
              onClick={handleToggle(value)}
              data-e2e={'transfer-list-' + e2e + '-item'}
            >
              <ListItemIcon>
                <Checkbox
                  data-e2e={'transfer-list-' + e2e + '-item-chk'}
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </ListItemIcon>
              <Tooltip title={data.description || data.name}>
                <ListItemText
                  id={labelId}
                  primary={data.name}
                  data-e2e={'transfer-list-' + e2e + '-item-text'}
                />
              </Tooltip>
            </ListItem>
          )
        })}
        <ListItem />
      </List>
    </Paper>
  )

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item>{customList(left, 'left')}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            data-e2e="transfer-btn-right-all"
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={left.length === 0}
            aria-label="move all right"
          >
            ≫
          </Button>
          <Button
            data-e2e="transfer-btn-right-chk"
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            data-e2e="transfer-btn-left-chk"
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
          <Button
            data-e2e="transfer-btn-left-all"
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={right.length === 0}
            aria-label="move all left"
          >
            ≪
          </Button>
        </Grid>
      </Grid>
      <Grid item>{customList(right, 'right')}</Grid>
    </Grid>
  )
}
